import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Luggage, MapPin, Calendar, FileCheck, FileSignature, Download, Compass, Ticket } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import SignContractDialog from '@/components/portal/SignContractDialog';
import api from '@/lib/api';
import { formatBRL, formatDate } from '@/lib/format';

export default function MyTrips() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(null);

  const load = () => {
    api.get('/portal/trips').then((r) => setTrips(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const downloadPdf = async (contractId, name) => {
    try {
      const r = await api.get(`/contracts/${contractId}/pdf`);
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${r.data.pdf_base64}`;
      link.download = `contrato-${name}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    } catch (err) {
      toast.error('Não foi possível baixar o contrato');
    }
  };

  const downloadVoucher = async (saleId, name) => {
    try {
      const r = await api.get(`/vouchers/${saleId}`);
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${r.data.pdf_base64}`;
      link.download = `voucher-${name}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    } catch (err) {
      toast.error('Não foi possível baixar o voucher');
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-2xl font-semibold">Minhas Viagens</h2>
        <p className="text-sm text-muted-foreground">Acompanhe suas viagens, valores e contratos.</p>
      </div>

      {loading ? <p className="text-muted-foreground">Carregando...</p> : trips.length === 0 ? (
        <Card className="p-12 text-center"><Compass className="h-10 w-10 mx-auto text-muted-foreground" /><p className="mt-4 text-muted-foreground">Você ainda não possui viagens cadastradas.</p><p className="text-sm text-muted-foreground mt-1">Entre em contato com a nossa equipe para planejar a sua próxima viagem.</p></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="portal-my-trips-list">
          {trips.map((t) => (
            <Card key={t.sale_id} className="overflow-hidden" data-testid="portal-trip-card">
              <div className="h-40 bg-muted">
                {t.foto ? <img src={t.foto} alt={t.trip_nome} className="h-full w-full object-cover" /> : <div className="h-full w-full section-wash flex items-center justify-center"><Compass className="h-8 w-8 text-primary/40" /></div>}
              </div>
              <div className="p-5">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {t.destino}</div>
                <h3 className="mt-1 font-semibold text-lg">{t.trip_nome}</h3>
                {t.data_inicio && <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><Calendar className="h-4 w-4" /> {formatDate(t.data_inicio)} {t.data_fim ? `- ${formatDate(t.data_fim)}` : ''}</div>}

                <div className="mt-4 flex items-center justify-between rounded-lg bg-muted/40 p-3">
                  <div><div className="text-xs text-muted-foreground">Valor total</div><div className="font-semibold tabular-nums">{formatBRL(t.valor_total)}</div></div>
                  <div className="text-right"><div className="text-xs text-muted-foreground">Pago</div><div className="font-semibold tabular-nums text-[hsl(174_55%_24%)]">{formatBRL(t.valor_pago)}</div></div>
                </div>

                <div className="mt-4 flex items-center justify-between gap-2">
                  {t.contrato_assinado ? (
                    <Badge className="bg-[hsl(174_55%_34%/0.15)] text-[hsl(174_55%_24%)] hover:bg-[hsl(174_55%_34%/0.15)] gap-1"><FileCheck className="h-3 w-3" /> Contrato assinado</Badge>
                  ) : (
                    <Badge variant="secondary">Contrato pendente</Badge>
                  )}
                  {t.contrato_assinado ? (
                    <Button variant="secondary" size="sm" onClick={() => downloadPdf(t.contract_id, t.trip_nome)} data-testid={`portal-download-contract-${t.sale_id}`}><Download className="h-4 w-4 mr-2" /> Baixar</Button>
                  ) : (
                    <Button size="sm" onClick={() => setSigning(t)} data-testid="portal-sign-contract-button"><FileSignature className="h-4 w-4 mr-2" /> Assinar contrato</Button>
                  )}
                </div>
                <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={() => downloadVoucher(t.sale_id, t.trip_nome)} data-testid={`portal-voucher-${t.sale_id}`}>
                  <Ticket className="h-4 w-4 mr-2" /> Baixar voucher da viagem
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <SignContractDialog open={!!signing} onOpenChange={(o) => !o && setSigning(null)} sale={signing} onSigned={() => { setSigning(null); load(); }} />
    </div>
  );
}
